// =============================================================================
// BP-07-03: ResourceDetailPage
// Full article view with content, metadata, and related resources
// =============================================================================

import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Share2,
  Printer,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { useResource, usePrefetchResource } from '@/features/resources/hooks/useResource';
import { useViewStore } from '@/features/resources/stores/resourceStore';
import {
  ResourceContent,
  ResourceBreadcrumb,
  ResourceMeta,
  RelatedResources,
  SaveButton,
  RatingWidget,
  extractTableOfContents,
} from '../components';
import { resourceTypeLabels } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addRecentlyViewed } = useViewStore();

  // Fetch resource
  const { data: resource, isLoading, error } = useResource(slug!);

  // Track view on load
  if (resource && typeof window !== 'undefined') {
    // Only add to recently viewed once
    addRecentlyViewed(resource.id);
  }

  // Extract table of contents
  const tableOfContents = resource?.content
    ? extractTableOfContents(resource.content)
    : [];

  // Handle share
  const handleShare = async () => {
    if (navigator.share && resource) {
      try {
        await navigator.share({
          title: resource.title,
          text: resource.excerpt || undefined,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-4 bg-warm-gray/20 rounded w-48" />
            <div className="h-10 bg-warm-gray/20 rounded w-3/4" />
            <div className="h-6 bg-warm-gray/20 rounded w-1/2" />
            <div className="space-y-3 pt-8">
              <div className="h-4 bg-warm-gray/20 rounded" />
              <div className="h-4 bg-warm-gray/20 rounded" />
              <div className="h-4 bg-warm-gray/20 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / 404 state
  if (error || !resource) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-navy mb-4">
            Resource Not Found
          </h1>
          <p className="text-warm-gray mb-6">
            The resource you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/resources"
            className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            Browse Resources
          </Link>
        </div>
      </div>
    );
  }

  // Check if external resource
  const isExternal = resource.resourceType === 'external';

  return (
    <>
      <Helmet>
        <title>{resource.metaTitle || resource.title} | BrightPath</title>
        <meta
          name="description"
          content={resource.metaDescription || resource.excerpt || ''}
        />
        {resource.featuredImageUrl && (
          <meta property="og:image" content={resource.featuredImageUrl} />
        )}
        <meta property="og:title" content={resource.title} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={resource.publishedAt || ''} />
        {resource.authorName && (
          <meta property="article:author" content={resource.authorName} />
        )}
      </Helmet>

      <div className="min-h-screen bg-cream print:bg-white">
        {/* Back Navigation */}
        <div className="bg-white border-b border-warm-gray/10 print:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="flex gap-12">
            {/* Main Content */}
            <article className="flex-1 min-w-0 max-w-4xl">
              {/* Breadcrumb */}
              <ResourceBreadcrumb resource={resource} className="mb-6 print:hidden" />

              {/* Header */}
              <header className="mb-8">
                {/* Type badge */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 text-sm font-medium bg-sage/10 text-sage rounded-full">
                    {resourceTypeLabels[resource.resourceType]}
                  </span>
                  {resource.isPremium && (
                    <span className="px-3 py-1 text-sm font-medium bg-terracotta/10 text-terracotta rounded-full">
                      Premium
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium text-navy mb-4 leading-tight">
                  {resource.title}
                </h1>

                {/* Excerpt */}
                {resource.excerpt && (
                  <p className="text-xl text-warm-gray leading-relaxed mb-6">
                    {resource.excerpt}
                  </p>
                )}

                {/* Meta */}
                <ResourceMeta resource={resource} variant="detailed" />

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 mt-6 print:hidden">
                  <SaveButton resourceId={resource.id} variant="default" />
                  
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-warm-gray/20 rounded-lg hover:border-sage hover:text-sage transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-warm-gray/20 rounded-lg hover:border-sage hover:text-sage transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="text-sm font-medium">Print</span>
                  </button>

                  {isExternal && resource.videoUrl && (
                    <a
                      href={resource.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span className="text-sm font-medium">View Original</span>
                    </a>
                  )}
                </div>
              </header>

              {/* Featured Image */}
              {resource.featuredImageUrl && (
                <figure className="mb-8 rounded-xl overflow-hidden">
                  <img
                    src={resource.featuredImageUrl}
                    alt={resource.title}
                    className="w-full h-auto"
                  />
                </figure>
              )}

              {/* Video embed */}
              {resource.videoUrl && resource.resourceType === 'video' && (
                <div className="mb-8 rounded-xl overflow-hidden aspect-video bg-navy">
                  <iframe
                    src={resource.videoUrl}
                    title={resource.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Content */}
              {resource.content && (
                <ResourceContent
                  content={resource.content}
                  className="mb-12"
                />
              )}

              {/* Attachments */}
              {resource.attachments && resource.attachments.length > 0 && (
                <section className="mb-12 p-6 bg-cream-dark rounded-xl print:hidden">
                  <h2 className="font-display text-lg font-semibold text-navy mb-4">
                    Downloadable Resources
                  </h2>
                  <ul className="space-y-3">
                    {resource.attachments.map((attachment: any, index: number) => (
                      <li key={index}>
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-white rounded-lg hover:shadow-soft transition-shadow"
                        >
                          <div className="w-10 h-10 rounded bg-sage/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-sage uppercase">
                              {attachment.type || 'PDF'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-navy truncate">
                              {attachment.name}
                            </div>
                            {attachment.size && (
                              <div className="text-sm text-warm-gray">
                                {attachment.size}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-warm-gray" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Rating Section */}
              <section className="mb-12 p-6 bg-white border border-warm-gray/10 rounded-xl print:hidden">
                <h2 className="font-display text-lg font-semibold text-navy mb-2">
                  Was this resource helpful?
                </h2>
                <p className="text-warm-gray mb-4">
                  Rate this resource to help other families find useful content.
                </p>
                <RatingWidget
                  resourceId={resource.id}
                  avgRating={resource.avgRating}
                  ratingCount={resource.ratingCount}
                  size="lg"
                  interactive
                />
              </section>

              {/* Related Resources (mobile) */}
              <div className="lg:hidden print:hidden">
                <RelatedResources
                  resourceId={resource.id}
                  diagnosisIds={resource.diagnosisIds}
                  categoryId={resource.category?.id}
                  limit={4}
                />
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block w-80 flex-shrink-0 print:hidden">
              <div className="sticky top-24 space-y-6">
                {/* Table of Contents */}
                {tableOfContents.length > 0 && (
                  <nav className="p-5 bg-white border border-warm-gray/10 rounded-xl">
                    <h3 className="font-display text-sm font-semibold text-navy mb-3 uppercase tracking-wide">
                      On This Page
                    </h3>
                    <ul className="space-y-2">
                      {tableOfContents.map((item) => (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            className={cn(
                              'block text-sm text-warm-gray hover:text-sage transition-colors',
                              item.level === 3 && 'pl-4'
                            )}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                {/* Related Resources */}
                <RelatedResources
                  resourceId={resource.id}
                  diagnosisIds={resource.diagnosisIds}
                  categoryId={resource.category?.id}
                  variant="sidebar"
                  limit={4}
                />

                {/* Diagnosis Links */}
                {resource.diagnosisIds && resource.diagnosisIds.length > 0 && (
                  <div className="p-5 bg-white border border-warm-gray/10 rounded-xl">
                    <h3 className="font-display text-sm font-semibold text-navy mb-3 uppercase tracking-wide">
                      Related Diagnoses
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {/* Note: Would need to fetch diagnosis data or pass it in */}
                      <Link
                        to={`/diagnoses/${resource.diagnosisIds[0]}`}
                        className="px-3 py-1.5 text-sm bg-cream hover:bg-cream-dark text-navy rounded-full transition-colors"
                      >
                        View Diagnosis Hub
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}

export default ResourceDetailPage;



